import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';
import {
  FieldDraftStore,
  fieldDraftConflicted,
  type FieldDraftBinding,
  type FieldDraftIdentity,
} from './field-drafts';
import { formatMoneyDisplay } from './money-display';
import { noteTextBoxBaseline } from './text-undo';
import './onboarding.css';

export function FieldDraftEditor({
  store,
  binding,
  value,
  disabled,
  multiline = false,
  placeholder,
  decimal,
  currency = false,
  label,
  suspendBlurCommit = false,
  onBegin,
  onEnd,
  onEnterKey,
  onSavedEnter,
}: {
  store: FieldDraftStore;
  binding: FieldDraftBinding;
  value: string;
  disabled: boolean;
  multiline?: boolean;
  placeholder?: string;
  decimal?: boolean;
  currency?: boolean;
  label?: string;
  suspendBlurCommit?: boolean;
  onBegin: () => void;
  onEnd?: () => void;
  onEnterKey?: () => void;
  onSavedEnter?: () => void;
}) {
  useSyncExternalStore(store.subscribe, store.getSnapshot);
  const retained = store.get(binding);
  const renamed = Boolean(retained && retained.name !== binding.name);
  const draft = renamed ? undefined : retained;
  const [admissionError, setAdmissionError] = useState<string>();
  const [focused, setFocused] = useState(false);
  // No "Enter confirms" hint inside the field: the owner called it wasted text that covered
  // values (2026-09-28). The shortcut sheet says what the keys do.
  const advance = useRef<(FieldDraftIdentity & { onSavedEnter?: () => void }) | null>(null);
  useEffect(
    () =>
      store.subscribeOutcome((identity, outcome) => {
        const pending = advance.current;
        if (!pending || pending.id !== identity.id || pending.revision !== identity.revision)
          return;
        advance.current = null;
        if (outcome.status === 'saved') pending.onSavedEnter?.();
      }),
    [store],
  );
  const raw = draft?.value ?? value;
  const shown = currency && !multiline && !focused && !draft ? formatMoneyDisplay(value) : raw;
  const conflicted = fieldDraftConflicted(draft, binding, value);
  const props = {
    value: shown,
    disabled: disabled || renamed || draft?.state === 'saving',
    placeholder,
    'aria-label': label ?? binding.name,
    inputMode: decimal ? ('decimal' as const) : undefined,
    title: currency && !multiline ? shown : undefined,
    onFocus: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (currency && !multiline) {
        const input = event.currentTarget;
        const selectedAll = input.selectionStart === 0 && input.selectionEnd === input.value.length;
        const wasFormatted = input.value !== raw;
        // Restore raw before browser replacement typing. Preserve a pre-focus
        // select-all rather than allowing the formatted/raw switch to append.
        flushSync(() => setFocused(true));
        noteTextBoxBaseline(input);
        if (selectedAll || (!draft && wasFormatted)) input.select();
      }
      onBegin();
    },
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      // Focus can precede another field's save. First typing belongs to the
      // revision now displayed; the store keeps any existing draft's binding.
      setAdmissionError(store.edit(binding, event.target.value)),
    onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(false);
      // Hiding a pane/layout can move browser focus, but it is not an edit
      // submission. Keep that draft in its existing store; explicit Confirm,
      // Enter, ordinary visible blur and document navigation retain their owners.
      if (!renamed && !suspendBlurCommit && event.currentTarget.getClientRects().length > 0)
        store.submit(binding);
      onEnd?.();
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Escape puts the field back to its saved value; the caret stays in the field.
      if (event.key === 'Escape' && draft && draft.state !== 'saving') {
        event.preventDefault();
        event.stopPropagation();
        store.discard(draft);
        return;
      }
      // Enter saves this one field's typing and moves on, and nothing else: it never vouches for a
      // value nobody changed (single confirm, 2026-10-05). Ctrl+Enter is the document's one main
      // button and is handled by the workspace.
      if (multiline || event.key !== 'Enter' || event.ctrlKey || event.metaKey || event.altKey)
        return;
      event.preventDefault();
      // Typing that ends where it began is no edit: drop it and move on, as Enter on an untouched
      // field does.
      if (draft && draft.state === 'editing' && draft.value === draft.baseValue) {
        store.discard(draft);
        onEnterKey?.();
        return;
      }
      if (draft) {
        advance.current = { ...draft, onSavedEnter };
        store.submit(draft);
      } else onEnterKey?.();
    },
  };
  return (
    <span className="field-draft-editor">
      {multiline ? (
        <textarea
          {...props}
          className="field-multiline"
          rows={Math.min(5, shown.split('\n').length)}
        />
      ) : (
        <input {...props} />
      )}
      {admissionError && (
        <small
          className="field-draft-conflict"
          role="alert"
          data-telemetry="error:field-edit-refused"
        >
          {admissionError}
        </small>
      )}
      {conflicted && draft && (
        <div className="field-draft-conflict" role="status">
          <small>
            {draft.reason ?? 'Not saved. Your typing is kept.'} Current value: {value || '(blank)'}
          </small>
          <button
            type="button"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => store.replace(binding)}
          >
            Save my value
          </button>
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => store.discard(draft)}
          >
            Use current value
          </button>
        </div>
      )}
    </span>
  );
}

/** Removed/renamed fields cannot silently take their unsaved text with them. */
export function RetainedFieldDrafts({
  store,
  documentId,
  fields,
  availableDocumentIds,
  workspaceUnavailable,
}: {
  store: FieldDraftStore;
  documentId: string;
  fields: readonly { id: string; name: string }[];
  /** The complete document index, not just hydrated/visible document details. */
  availableDocumentIds: readonly string[];
  workspaceUnavailable?: boolean;
}) {
  const drafts = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const unavailable = drafts.filter(
    (draft) =>
      !availableDocumentIds.includes(draft.documentId) ||
      (draft.documentId === documentId &&
        !fields.some((field) => field.id === draft.fieldId && field.name === draft.name)),
  );
  if (!unavailable.length) return null;
  return (
    <details className="retained-field-drafts">
      <summary>Unsaved changes · {unavailable.length}</summary>
      {unavailable.map((draft) => (
        <div className="field-draft-conflict" key={draft.id}>
          <small>
            {workspaceUnavailable
              ? `${draft.name} · document not open. This text has not been saved; you can copy or discard it.`
              : availableDocumentIds.includes(draft.documentId)
                ? `${draft.name} changed or was removed. Copy or discard this typing before editing its replacement.`
                : `${draft.name} · document removed. This text has not been saved; you can copy or discard it.`}
          </small>
          <pre>{draft.value}</pre>
          <button
            type="button"
            disabled={draft.state === 'saving'}
            onClick={() => store.discard(draft)}
          >
            Discard unsaved typing
          </button>
        </div>
      ))}
    </details>
  );
}
